import { IconButton } from '@affine/component';
import { useActiveBlocksuiteEditor } from '@affine/core/components/hooks/use-block-suite-editor';
import { DocsService } from '@affine/core/modules/doc';
import { WorkbenchService } from '@affine/core/modules/workbench';
import { CloseIcon } from '@blocksuite/icons/rc';
import { useService } from '@toeverything/infra';
import { useCallback, useMemo, useState } from 'react';

import { useBranchGraph } from '../hooks/use-branch-graph';
import * as styles from './branches-floating-panel.css';
import { formatRelativeDate } from './branches-inline-panel';

interface BranchSummary {
  branchDocId: string;
  title: string;
  createdAt: string;
}

/**
 * Persistent right-rail affordance for the active doc's branches.
 *
 * The inline `BranchesInlinePanel` lives at the bottom of the doc body
 * and scrolls out of view on long docs — so a researcher mid-document
 * loses any way to see or jump to their branches. This floating toggle
 * (a sibling of the canvas-outline toggle) restores always-available
 * access: it appears only when the current doc has branches and opens a
 * compact list that jumps to any branch in one click.
 *
 * Mounted once at the workspace-layout level alongside
 * `MindmapOutlinePanel`. Reads the same `useBranchGraph()` source as the
 * inline panel, so it refreshes on create/undo/trash automatically.
 */
export function BranchesFloatingPanel() {
  const [activeEditor] = useActiveBlocksuiteEditor();
  const docsService = useService(DocsService);
  const workbench = useService(WorkbenchService).workbench;
  const branchGraph = useBranchGraph();
  const [open, setOpen] = useState(false);

  const docId = (activeEditor?.host?.std?.store as { id?: string } | undefined)
    ?.id;

  const branches = useMemo<BranchSummary[]>(() => {
    if (!docId) return [];
    const entries = branchGraph.getBranches(docId);
    if (entries.length === 0) return [];
    return entries.map(entry => {
      // Read the latest title from the branch's DocRecord so renames
      // appear here without a reload.
      let title = 'Untitled branch';
      try {
        const record = docsService.list.doc$(entry.branchDocId).value;
        const metaTitle = record?.meta$.value.title?.trim();
        if (metaTitle) title = metaTitle;
      } catch {
        /* keep default */
      }
      return {
        branchDocId: entry.branchDocId,
        title,
        createdAt: entry.createdAt,
      };
    });
  }, [branchGraph, docId, docsService]);

  const openBranch = useCallback(
    (branchDocId: string) => {
      workbench.openDoc({ docId: branchDocId });
    },
    [workbench]
  );

  // Only surface the affordance when the active doc actually has
  // branches — mirrors the inline panel's "no empty heading" rule and
  // keeps the toggle off every other doc/view.
  if (branches.length === 0) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        className={styles.toggle}
        data-open={open ? 'true' : undefined}
        onClick={() => setOpen(o => !o)}
        title={open ? 'Hide branches' : `Show branches (${branches.length})`}
        aria-label="Toggle branches panel"
      >
        <BranchGlyph />
      </button>

      {open && (
        <aside className={styles.panel} aria-label="Branches">
          <header className={styles.header}>
            <span className={styles.headerTitle}>
              Branches · {branches.length}
            </span>
            <IconButton
              size="20"
              variant="plain"
              onClick={() => setOpen(false)}
              aria-label="Close branches panel"
            >
              <CloseIcon />
            </IconButton>
          </header>
          <ol className={styles.list}>
            {branches.map(branch => (
              <li key={branch.branchDocId}>
                <button
                  type="button"
                  className={styles.row}
                  onClick={() => openBranch(branch.branchDocId)}
                >
                  <span className={styles.rowTitle}>{branch.title}</span>
                  {branch.createdAt ? (
                    <span className={styles.rowDate}>
                      {formatRelativeDate(branch.createdAt)}
                    </span>
                  ) : null}
                </button>
              </li>
            ))}
          </ol>
        </aside>
      )}
    </>
  );
}

/** Simple branch/fork glyph — inlined to avoid an icon-package dependency. */
function BranchGlyph() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="6" cy="5" r="2.4" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="6" cy="19" r="2.4" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="18" cy="9" r="2.4" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M6 7.4v9.2"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path
        d="M6 12h6a4 4 0 0 0 4-4"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}
